import * as React from 'react';
import { catalogLongCardDate, formatShortTimeOnly } from '@/utils/dates';
import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import classNames from 'classnames';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import fromUnixTime from 'date-fns/fromUnixTime';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBuyNow } from '@/redux/modules/buyNow';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getTotalBidLimitBalance } from '@/redux/modules/bidLimit';
import isEqual from 'react-fast-compare';
import isToday from 'date-fns/isToday';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import styles from './RegistrationMessage.module.scss';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    className?: string;
    itemId: number | null | undefined;
    showSendSellerMessageModalDeclined: (event: React.SyntheticEvent) => void;
};

const RegistrationMessage = (props: Props) => {
    const { catalogId, className, itemId, showSendSellerMessageModalDeclined } = props;

    const { catalogStatus = '', currency = 'usd', saleStartTs = 0 } = useSelector(
        (state) => getCatalog(state, catalogId),
        isEqual
    );
    const { isSold = false } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { isCatalogOnly } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const catalogRegistration = useSelector((state) => getCatalogRegistration(state, catalogId), isEqual);
    const { status: buyNowStatus } = useSelector((state) => getBuyNow(state, itemId), isEqual);
    const { bidderHasBid = false } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const totalCatalogBidLimitBalance = useSelector((state) => getTotalBidLimitBalance(state, catalogId));

    const { approved, bidLimit, declined, limited, pending, registered, suspended } = catalogRegistration;

    const buyNowAvailable = buyNowStatus === 'available' && catalogStatus === 'done';
    const isCatalogLive = catalogStatus === 'live';

    const contactAuctioneer = 'registration.contact_auctioneer';

    const auctioneerDeclineMessage = (
        <ActionText className={styles.declinedMessage} onClick={showSendSellerMessageModalDeclined}>
            <FormattedMessage id={contactAuctioneer} />
        </ActionText>
    );

    let showRegisterLink = false;
    let registrationMessage;
    let extraValues: any = {
        contactAuctioneer: auctioneerDeclineMessage,
        date: <span className={styles.strong}>{catalogLongCardDate(saleStartTs)}</span>,
        today: isToday(fromUnixTime(saleStartTs)) ? (
            <span className={styles.strong}>
                <FormattedMessage
                    id="registration.liveMessages.today"
                    values={{ time: formatShortTimeOnly(saleStartTs) }}
                />
            </span>
        ) : (
            <span className={styles.strong}>
                <FormattedMessage
                    id="registration.liveMessages.yesterday"
                    values={{ time: formatShortTimeOnly(saleStartTs) }}
                />
            </span>
        ),
    };

    if (isCatalogLive) {
        if (pending) {
            registrationMessage = 'registration.liveMessages.pending';
        } else if (approved && !limited) {
            registrationMessage = 'registration.liveMessages.approved';
        } else if (limited) {
            registrationMessage = 'registration.liveMessages.limited';
            extraValues = {
                ...extraValues,
                balance: (
                    <span className={classNames(styles.strong, styles.bidLimitAmount)}>
                        <FormattedCurrency
                            convert
                            noFraction
                            sourceCurrency={currency}
                            value={totalCatalogBidLimitBalance}
                        />
                    </span>
                ),
                limit: (
                    <span className={classNames(styles.strong, styles.bidLimitAmount)}>
                        <FormattedCurrency convert noFraction sourceCurrency={currency} value={bidLimit} />
                    </span>
                ),
            };
        } else if (declined) {
            registrationMessage = 'registration.liveMessages.declined';
        } else if (suspended) {
            registrationMessage = 'registration.liveMessages.suspended';
        } else if (!registered) {
            registrationMessage = 'registration.liveMessages.notRegistered';
            extraValues = {
                ...extraValues,
                register: (
                    <RegisterLink
                        catalogId={catalogId}
                        itemId={itemId}
                        registerToBidMessageId="registration.register"
                    />
                ),
            };
        }
    } else {
        if (suspended) {
            registrationMessage = 'registration.suspended_with_date';
        } else if (declined) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.declined';
            } else {
                registrationMessage = 'registration.declined_with_date';
            }
        } else if (approved && !isCatalogOnly) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.approved';
            } else {
                registrationMessage = 'registration.auction_status_approved';
            }
        } else if (limited && !isCatalogOnly) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.approved';
            } else {
                registrationMessage = 'registration.auction_status_approved_limited';
                extraValues = {
                    ...extraValues,
                    balance: (
                        <span className={classNames(styles.strong, styles.bidLimitAmount)}>
                            <FormattedCurrency
                                convert
                                noFraction
                                sourceCurrency={currency}
                                value={totalCatalogBidLimitBalance}
                            />
                        </span>
                    ),
                    limit: (
                        <span className={classNames(styles.strong, styles.bidLimitAmount)}>
                            <FormattedCurrency convert noFraction sourceCurrency={currency} value={bidLimit} />
                        </span>
                    ),
                };
            }
        } else if ((pending || (pending === undefined && bidderHasBid)) && !isCatalogOnly) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.pending';
            } else {
                registrationMessage = 'registration.auction_status_pending';
            }
        } else if (!isCatalogOnly) {
            if (!isSold) {
                showRegisterLink = true;
                registrationMessage = 'registration.auction_status_not_registered';
            }
        }
    }

    const message = registrationMessage ? (
        <FormattedMessage id={registrationMessage} tagName="span" values={{ ...extraValues }} />
    ) : null;

    const classes = classNames(styles.registrationMessage, className);

    return !buyNowAvailable ? (
        <div className={classes}>
            {message}
            {showRegisterLink && (
                <RegisterLink catalogId={catalogId} itemId={itemId} registerToBidMessageId="registration.register" />
            )}
        </div>
    ) : null;
};

export default React.memo<Props>(RegistrationMessage, isEqual);
